import { Component, useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import Lenis from "lenis";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Servizi from "@/pages/Servizi";
import Metodo from "@/pages/Metodo";
import Prenota from "@/pages/Prenota";
import Contatti from "@/pages/Contatti";
import { Privacy, CookiePolicy } from "@/pages/Legal";
import Admin from "@/pages/Admin";
import AdminAgenda from "@/pages/AdminAgenda";
import AdminWeeklyAgenda from "@/pages/AdminWeeklyAgenda";
import AdminReset from "@/pages/AdminReset";
import PublicPage from "@/pages/PublicPage";

/* Draft-preview flag: set before any query fires when the URL carries ?preview=1. */
if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1") {
  try {
    sessionStorage.setItem("kn_preview", "1");
  } catch (e) {
    /* private mode */
  }
}
import NotFound from "@/pages/NotFound";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-ivory text-charcoal px-6">
          <div className="text-center max-w-md">
            <p className="font-display text-4xl mb-4">Ops, qualcosa è andato storto.</p>
            <a href="/" className="text-deeptaupe underline">Torna alla home</a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

function SiteShell({ children }) {
  return (
    <>
      <Navbar />
      <main id="main">{children}</main>
      <Footer />
    </>
  );
}

function PreviewBanner() {
  const [on, setOn] = useState(
    typeof window !== "undefined" && sessionStorage.getItem("kn_preview") === "1",
  );
  if (!on) return null;
  return (
    <div
      className="fixed bottom-4 inset-x-4 z-[60] flex flex-wrap items-center justify-center gap-3 rounded-full bg-charcoal text-ivory px-6 py-3 shadow-soft"
      data-testid="preview-banner"
    >
      <span className="text-[12px] uppercase tracking-widest2">Anteprima della bozza — modifiche non pubblicate</span>
      <button
        onClick={() => {
          sessionStorage.removeItem("kn_preview");
          window.location.href = "/";
        }}
        data-testid="preview-exit"
        className="text-[12px] uppercase tracking-widest2 underline underline-offset-2"
      >
        Esci dall'anteprima
      </button>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  return isAdmin ? (
    <Routes>
      <Route path="/admin" element={<Admin />} />
      <Route path="/admin/agenda/:date" element={<AdminAgenda />} />
      <Route path="/admin/agenda-settimana/:date" element={<AdminWeeklyAgenda />} />
      <Route path="/admin/reset" element={<AdminReset />} />
    </Routes>
  ) : (
    <SiteShell>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/chi-sono" element={<About />} />
        <Route path="/servizi" element={<Servizi />} />
        <Route path="/il-metodo" element={<Metodo />} />
        <Route path="/prenota" element={<Prenota />} />
        <Route path="/contatti" element={<Contatti />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/pagine/:slug" element={<PublicPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </SiteShell>
  );
}

export default function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    lenisRef.current = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <MotionConfig reducedMotion="user">
        <BrowserRouter>
          <ScrollToTop />
          <PreviewBanner />
          <AppRoutes />
        </BrowserRouter>
      </MotionConfig>
    </ErrorBoundary>
  );
}
